import { COLOR_RULE } from "./indicatorTypes";
import { isAtMost } from "./math/comparisons";

const SEGMENT_COLOR_OFFSET = 4;
const SHAPE_COLUMN = 0;

/**
 * Colour slots of the histogram palette (indicatorRenderer): two per
 * histogram plot (up, down) and the last one transparent.
 */
export const TRANSPARENT_SLOT = 7;
export const SLOTS_PER_HISTOGRAM = 2;

/**
 * Line segments of a plot between neighbouring values (value i to i + 1),
 * for lineRenderer; prices relative to `priceBase`, x moved by the plot
 * shift. A segment without both values stays as a gap: zero width,
 * transparent (paintLines skips it).
 *
 * @param {object} params
 * @param {number[]} params.values
 * @param {number} params.shift - bars
 * @param {number} params.firstSegment - index of the first value of the first segment
 * @param {number} params.segmentCount
 * @param {number} params.priceBase
 * @param {number[]} params.color - RGBA 0..1
 * @param {number} params.width - CSS px
 * @param {number} params.floatsPerLine - lineRenderer.floatsPerInstance
 * @returns {Float32Array}
 */
export const buildLineSegments = ({
  values,
  shift,
  firstSegment,
  segmentCount,
  priceBase,
  color,
  width,
  floatsPerLine,
}) => {
  const data = new Float32Array(Math.max(0, segmentCount) * floatsPerLine);

  for (let position = 0; position < segmentCount; position += 1) {
    const index = firstSegment + position;
    const from = values[index];
    const to = values[index + 1];
    const offset = position * floatsPerLine;

    data[offset] = index + shift;
    data[offset + 2] = index + 1 + shift;

    if (Number.isFinite(from) && Number.isFinite(to)) {
      data[offset + 1] = from - priceBase;
      data[offset + 3] = to - priceBase;
      data.set(color, offset + SEGMENT_COLOR_OFFSET);
      data[offset + floatsPerLine - 1] = width;
    }
  }

  return data;
};

const isUp = (colorRule, values, bars, index) => {
  if (colorRule === COLOR_RULE.BAR) return bars[index].close >= bars[index].open;
  if (colorRule === COLOR_RULE.TREND) return !isAtMost(values[index], values[index - 1]);

  return true;
};

/**
 * Histogram columns from zero to each value, for rectRenderer.
 *
 * @param {object} params
 * @param {number[]} params.values
 * @param {object[]} params.bars - for COLOR_RULE.BAR
 * @param {string} params.colorRule - COLOR_RULE value
 * @param {number} params.upSlot - palette slot of the up colour, the down colour is the next one
 * @param {number} params.shift - bars
 * @param {number} params.firstIndex
 * @param {number} params.count
 * @param {number} params.priceBase
 * @param {number} params.floatsPerRect - rectRenderer.floatsPerInstance
 * @returns {Float32Array}
 */
export const buildHistogramColumns = ({
  values,
  bars,
  colorRule,
  upSlot,
  shift,
  firstIndex,
  count,
  priceBase,
  floatsPerRect,
}) => {
  const data = new Float32Array(Math.max(0, count) * floatsPerRect);

  for (let position = 0; position < count; position += 1) {
    const index = firstIndex + position;
    const value = values[index];
    const hasValue = Number.isFinite(value);
    const offset = position * floatsPerRect;

    data[offset] = index + shift;
    data[offset + 1] = -priceBase;
    data[offset + 2] = (hasValue ? value : 0) - priceBase;
    data[offset + 3] = SHAPE_COLUMN;

    if (!hasValue) data[offset + 4] = TRANSPARENT_SLOT;
    else data[offset + 4] = isUp(colorRule, values, bars, index) ? upSlot : upSlot + 1;
  }

  return data;
};

/**
 * Price the instances of an indicator are relative to, so float32 keeps
 * prices like BTC 80,000.01 exact: the last value of the first plot that has one.
 *
 * @param {Array<{ key: string }>} plots
 * @param {Object<string, number[]>} series
 * @returns {number}
 */
export const lastFiniteValue = (plots, series) => {
  for (let plotIndex = 0; plotIndex < plots.length; plotIndex += 1) {
    const values = series[plots[plotIndex].key];

    for (let index = values.length - 1; index >= 0; index -= 1) {
      if (Number.isFinite(values[index])) return values[index];
    }
  }

  return 0;
};

/**
 * @param {Float32Array[]} chunks - instances of several plots
 * @returns {Float32Array} one after another
 */
export const concatInstances = (chunks) => {
  const data = new Float32Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0));

  chunks.reduce((offset, chunk) => {
    data.set(chunk, offset);

    return offset + chunk.length;
  }, 0);

  return data;
};
